// =====================================================
// FileUploadZone Component
// AdminMunicipal v6.0 - Document Storage System
// Security: Enhanced with magic number validation
// =====================================================

import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, File, AlertCircle, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { validateUploadFile, sanitizeFilename } from '../../lib/validation/fileValidation';
import { logger } from '../../lib/logger';

interface FileUploadZoneProps {
  entityType: 'propuesta' | 'gestion' | 'reunion';
  entityId: string;
  onUploadComplete?: () => void;
  maxFileSize?: number; // in bytes, default 100MB
  acceptedFileTypes?: string[]; // MIME types
  disabled?: boolean;
}

interface UploadingFile {
  file: File;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
  attachmentId?: string;
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  entityType,
  entityId,
  onUploadComplete,
  maxFileSize = 104857600, // 100MB
  acceptedFileTypes,
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { currentOrg } = useOrganization();

  // Handle drag events
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files: File[] = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, [disabled]);

  // Validate file with comprehensive security checks
  const validateFile = async (file: File): Promise<{ valid: boolean; error?: string }> => {
    // Use comprehensive validation that includes:
    // - File size check
    // - Dangerous extension blacklist
    // - Allowed extension whitelist
    // - MIME type verification
    // - Magic number (file signature) validation
    return await validateUploadFile(
      file,
      acceptedFileTypes || [],
      maxFileSize
    );
  };

  // Upload file to R2
  const uploadFile = async (file: File) => {
    if (!user || !currentOrg) {
      logger.error('[FileUploadZone] Upload failed: No user or organization');
      throw new Error('Usuario no autenticado o sin organización');
    }

    // Sanitize filename to prevent path traversal and other attacks
    const safeFileName = sanitizeFilename(file.name);

    // Create uploading file entry
    const uploadingFile: UploadingFile = {
      file,
      progress: 0,
      status: 'uploading',
    };

    setUploadingFiles((prev) => [...prev, uploadingFile]);

    try {
      // Step 1: Get presigned upload URL from Edge Function
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No se pudo obtener el token de autenticación');
      }

      const uploadUrlResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-upload-url`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            entityType,
            entityId,
            organizationId: currentOrg.id,
            fileName: safeFileName,
            fileSize: file.size,
            mimeType: file.type || 'application/octet-stream',
          }),
        }
      );

      if (!uploadUrlResponse.ok) {
        const errorData = await uploadUrlResponse.json();
        throw new Error(errorData.error || 'Error al generar URL de subida');
      }

      const { uploadUrl, storagePath, bucketName } = await uploadUrlResponse.json();

      // Step 2: Upload file to R2 using presigned URL
      // NOTE: Do NOT send Content-Type header with presigned URLs
      // The header must match the signature, and our presigned URL doesn't include it
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Error al subir el archivo a R2');
      }

      // Update progress to 90%
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.file === file ? { ...f, progress: 90 } : f
        )
      );

      // Step 3: Save metadata to database
      const attachmentTableMap = {
        propuesta: 'propuesta_attachments',
        gestion: 'gestion_attachments',
        reunion: 'reunion_attachments',
      };

      const entityIdFieldMap = {
        propuesta: 'propuesta_id',
        gestion: 'gestion_id',
        reunion: 'reunion_id',
      };

      const { data: attachment, error: dbError } = await supabase
        .from(attachmentTableMap[entityType] as any)
        .insert({
          [entityIdFieldMap[entityType]]: entityId,
          organization_id: currentOrg.id,
          file_name: safeFileName,
          file_size: file.size,
          mime_type: file.type || 'application/octet-stream',
          r2_bucket_name: bucketName,
          r2_storage_path: storagePath,
          uploaded_by: user.id,
        } as any)
        .select()
        .single();

      if (dbError) {
        logger.error('Error saving attachment metadata:', dbError);
        throw new Error('Error al guardar los datos del archivo');
      }

      // Update to success
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.file === file
            ? { ...f, progress: 100, status: 'success', attachmentId: (attachment as any)?.id }
            : f
        )
      );

      // Remove from list after 2 seconds
      setTimeout(() => {
        setUploadingFiles((prev) => prev.filter((f) => f.file !== file));
        if (onUploadComplete) {
          onUploadComplete();
        }
      }, 2000);
    } catch (error) {
      logger.error('Upload error:', error);
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.file === file
            ? {
                ...f,
                status: 'error',
                error: error instanceof Error ? error.message : 'Error desconocido',
              }
            : f
        )
      );
    }
  };

  // Handle files with async validation
  const handleFiles = async (files: File[]) => {
    for (const file of files) {
      // Perform async validation (includes magic number check)
      const validation = await validateFile(file);
      if (!validation.valid) {
        // Show error
        const errorFile: UploadingFile = {
          file,
          progress: 0,
          status: 'error',
          error: validation.error,
        };
        setUploadingFiles((prev) => [...prev, errorFile]);

        // Remove error after 5 seconds
        setTimeout(() => {
          setUploadingFiles((prev) => prev.filter((f) => f.file !== file));
        }, 5000);
        continue;
      }

      // Upload valid file
      uploadFile(file);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent event from bubbling to parent modal backdrop
    e.stopPropagation();

    if (e.target.files && e.target.files.length > 0) {
      const files: File[] = Array.from(e.target.files);

      // Process files asynchronously to prevent blocking the UI
      setTimeout(() => {
        handleFiles(files);
      }, 0);

      // Reset input immediately to allow re-selecting same file
      e.target.value = '';
    }
  };

  // Handle click to select files
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from bubbling to parent modal backdrop
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  // Remove uploading file
  const removeUploadingFile = (file: File) => {
    setUploadingFiles((prev) => prev.filter((f) => f.file !== file));
  };

  return (
    <div
      className="space-y-4"
      onClick={(e) => e.stopPropagation()} // Prevent any clicks from bubbling to modal backdrop
    >
      {/* Upload Zone */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          handleClick(e);
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onTouchStart={(e) => {
          // Prevent Android from treating this as a navigation gesture
          e.stopPropagation();
        }}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
          ${isDragging ? 'border-[#B89658] bg-[#B89658]/5 scale-[1.02]' : 'border-gray-300 hover:border-[#B89658]/50'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          onFocus={(e) => {
            e.stopPropagation();
          }}
          onBlur={(e) => {
            e.stopPropagation();
          }}
          className="hidden"
          disabled={disabled}
          accept={acceptedFileTypes?.join(',')}
        />

        <div className="flex flex-col items-center space-y-3">
          <div className={`p-3 rounded-full ${isDragging ? 'bg-[#B89658]/10' : 'bg-gray-100'}`}>
            <Upload className={`w-8 h-8 ${isDragging ? 'text-[#B89658]' : 'text-gray-400'}`} />
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">
              {isDragging ? 'Suelta los archivos aquí' : 'Arrastra archivos o haz clic para seleccionar'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Máximo {Math.round(maxFileSize / 1048576)}MB por archivo
            </p>
          </div>
        </div>
      </div>

      {/* Uploading Files List */}
      <AnimatePresence>
        {uploadingFiles.length > 0 && (
          <div className="space-y-2">
            {uploadingFiles.map((uploadingFile, index) => (
              <motion.div
                key={`${uploadingFile.file.name}-${index}`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className={`
                  p-4 rounded-lg border
                  ${uploadingFile.status === 'error' ? 'border-red-200 bg-red-50' :
                    uploadingFile.status === 'success' ? 'border-green-200 bg-green-50' :
                    'border-gray-200 bg-white'}
                `}
              >
                <div className="flex items-start space-x-3">
                  {/* File Icon */}
                  <div className={`
                    p-2 rounded-lg
                    ${uploadingFile.status === 'error' ? 'bg-red-100' :
                      uploadingFile.status === 'success' ? 'bg-green-100' :
                      'bg-blue-100'}
                  `}>
                    {uploadingFile.status === 'error' ? (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    ) : uploadingFile.status === 'success' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <File className="w-5 h-5 text-blue-600" />
                    )}
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {uploadingFile.file.name}
                      </p>
                      <button
                        onClick={() => removeUploadingFile(uploadingFile.file)}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <p className="text-xs text-gray-500">
                      {(uploadingFile.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>

                    {/* Progress Bar */}
                    {uploadingFile.status === 'uploading' && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <motion.div
                            className="bg-[#B89658] h-1.5 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${uploadingFile.progress}%` }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Subiendo... {uploadingFile.progress}%
                        </p>
                      </div>
                    )}

                    {/* Error Message */}
                    {uploadingFile.status === 'error' && (
                      <p className="text-xs text-red-600 mt-1">
                        {uploadingFile.error}
                      </p>
                    )}

                    {/* Success Message */}
                    {uploadingFile.status === 'success' && (
                      <p className="text-xs text-green-600 mt-1">
                        Archivo subido exitosamente
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
